<template>
  <a
    class="button"
    @click="internalButtonClick"
    :class="classObject">
    <!-- Whitespace, for when text is displayed-->
    <em>&nbsp;</em>

    <!-- Icon, if available-->
    <svg v-if="icon"><use :xlink:href="'#' + icon"></use></svg>

    <!-- Text, if available -->
    <span
      v-if="text"
      class="flex align-center">{{text}}</span>

    <!-- Checkbox for toggle functionality -->
    <label
      v-if="toggle"
      class="button-toggler">
      <input
        v-model="isToggled"
        type="checkbox">
    </label>

<!--    Fill progress -->
    <div
      v-if="fills"
      class="fill-progress"
      :class="{complete: fillStarted}"></div>

    <!-- Right icon -->
    <svg
      v-if="rightIcon"
      class="right-icon"><use :xlink:href="'#' + rightIcon"></use></svg>

    <!-- Right text -->
    <div
      v-if="rightText"
      class="flex items-center ml-auto">
      {{ rightText }}
    </div>

    <!-- Slot for including other stuff -->
    <slot></slot>
  </a>
</template>

<script>
  export default {
    name: 'Btn',
    props: {
      /* used for toggle buttons */
      value: {
        type: Boolean,
        default: false
      },
      text: [String, Number],
      icon: String,
      medium: Boolean,
      big: Boolean,
      tight: Boolean,
      small: {
        type: Boolean,
        default: false
      },
      toggle: {
        type: Boolean,
        default: false
      },
      error: Boolean,
      success: Boolean,
      warning: Boolean,
      pending: Boolean, // new state needed by cockpit
      noTransform: Boolean,
      invert: Boolean,
      blueText: Boolean,
      textOnly: Boolean,
      redIcon: Boolean,
      centerIcon: Boolean,
      center: {
        type: Boolean,
        default: false
      },
      square: {
        /* removes the padding and makes it a square button */
        type: Boolean,
        default: false
      },
      round: {
        /* used on the settings pages inside lists */
        type: Boolean,
        default: false
      },
      to: {
        /* provides link to another page */
        type: [String, Object]
      },
      rightIcon: {
        /* used for green checkmark mostly */
        type: String
      },
      addToList: {
        /* used in a list of vertical buttons, to add new items */
        type: Boolean
      },
      fills: {
        /* used on the scan button to show progress on the button directly */
        type: Boolean,
        default: false
      },
      fillStarted: {
        /* signals the fill operation to start */
        type: Boolean,
        default: false
      },
      forceWrap: Boolean,
      largeBorder: Boolean,
      mediumBorder: Boolean,
      invertHover: Boolean,
      rightText: {
        type: String,
        default: ''
      },
      ellipsis: Boolean
    },
    data () {
      return {
        isToggled: false
      }
    },
    computed: {
      classObject () {
        return {
          big: this.big,
          medium: this.medium,
          tight: this.tight,
          blueText: this.blueText,
          redIcon: this.redIcon,
          small: this.small,
          error: this.error,
          success: this.success,
          warning: this.warning,
          pending: this.pending,
          noTransform: this.noTransform,
          invert: this.invert,
          square: this.square,
          center: this.center,
          centerIcon: this.centerIcon,
          toggled: this.isToggled,
          round: this.round,
          textOnly: this.textOnly,
          addToList: this.addToList,
          forceWrap: this.forceWrap,
          largeBorder: this.largeBorder,
          mediumBorder: this.mediumBorder,
          invertHover: this.invertHover,
          ellipsis: this.ellipsis
        }
      }
    },
    methods: {
      internalButtonClick () {
        if (this.to) {
          this.$router.push(this.to)
        }
      }
    },
    watch: {
      value: {
        immediate: true,
        handler (val) { this.isToggled = val }
      },
      isToggled (val) {
        this.$emit('input', val)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .button {
    position: relative;
    background-color: $white;
    color: $gray-dark;
    border: 1px solid $cyan;
    height: $size-grid / 2;
    /*<!--line-height: $size-grid / 2;-->*/
    display: inline-flex;
    padding: 0 $size-grid / 4;
    font-family: 'SourceSansSemiBold';
    letter-spacing: 0.025rem;
    cursor: pointer;
    text-transform: uppercase;
    transition: background-color 100ms ease-in;

    &:hover {
      /*<!--background-color: $cyan;-->*/
      /*<!--color: $white;-->*/
      >svg {
        fill: darken($cyan, 15%);
      }
    }

    &:active,
    &.active {
      background-color: $cyan;
      >svg {
        fill: $white;
      }

      span {
        color: $white;
      }
    }

    >em {
      width: 0;
    }

    >svg {
      width: 2.5rem;
      height: 2.5rem;
      fill: $cyan;

      // text and icon
      + span {
        margin-left: 1rem;
      }
    }

    &.icon-wide {
      &.no-pad {
        padding-left: 0;
        padding-right: 0;
      }
      >svg {
        width: 4.5rem;
      }
    }

    /* Style variations */
    &.noTransform {
      text-transform: none;
    }

    &.medium {
      height: $size-grid / 1.5;
      align-items: center;
      >svg {
        transform: scale(1.5);
      }
    }

    &.big {
      height: $size-grid;
      align-items: center;
      >svg {
        transform: scale(1.45);
      }
    }

    &.tight {
      padding: 0 $size-grid / 8;

      >svg + span {
        margin-left: 0.25rem;
      }
    }

    &.redIcon {
      border-color: $color-error;
      color: $color-error;
      >svg {
        fill: $color-error;
      }

      &:hover {
        background-color: $color-error;
        color: $white;
        >svg {
          fill: $white;
        }
      }

      &:active {
        border-color: darken($color-error, 15%);
        background-color: darken($color-error, 15%);
        >svg {
          fill: $white;
        }
      }
    }

    &.blueText {
      color: $cyan;

      &:hover {
        color: darken($cyan, 15%);
      }
    }

    &.small {
      height: $size-grid / 2;
      width: $size-grid / 2;
      align-items: center;
      >svg {
        /*transform: scale(0.5);*/
        position: absolute;
        top: 0;
        left: calc(50% - 20px);
      }
    }

    &.square {
      padding-left: 0;
      padding-right: 0;
    }

    &.center {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.textOnly {
      em {
        display: none;
      }

      span {
        display: block;
        text-align: center;
        padding: 0.563rem 0;
      }
    }

    &.centerIcon {
      >svg {
        position: absolute;
        top: 0;
        left: calc(50% - 20px);
      }
    }

    &.round {
      border-radius: 0.5rem;
      color: $cyan;
      width: calc(100% - #{$size-grid / 4});
      font-size: 1.3rem;
      text-transform: none;

      &:active {
        color: $white;
      }

      &:hover {
        color: darken($cyan, 15%);
      }
    }

    &.addToList {
      display: block;
      width: 4rem;
      margin: 0 auto;
      transform: translateX(-0.5rem);
    }

    &.largeBorder {
      border-width: 4px;
    }

    &.mediumBorder {
      border-width: 2px;
    }

    &.forceWrap {
      padding: 0 $size-grid / 16;
      > span {
        word-break: break-word;
        text-align: center;
      }
    }

    /* Functionality */
    .button-toggler {
      background: transparent;
      cursor: pointer;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;

      > input {
        -webkit-appearance: none;
      }
    }

    &.ellipsis {
      span {
        display: block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 2.8rem;
      }
    }

    /* Toggled state */
    &.toggled {
      background: $cyan;

      >svg {
        fill: $white;
      }
    }

    /* Colors */

    &.invert {
      background: $cyan;

      >svg {
        fill: $white;
      }

      span {
        color: $white;
      }

      &:hover {
        background: darken($cyan, 15%);
      }
    }

    &.invertHover {
      &:hover {
        background: $cyan;

        >svg {
          fill: $white;
        }

        span {
          color: $white;
        }
      }
    }

    &.success {
      background: $color-success;
      border-color: $color-success;
      color: $white;

      >svg {
        fill: $white;
      }

      &:hover {
        background: darken($color-success, 15%);
        color: darken($white, 15%);

        svg {
          fill: $gray-very-light;
        }
      }

      &:active {
        background: darken($color-success, 15%);
        svg {
          fill: $white;
        }
      }

      &[disabled] {
        border-color: $gray-light;
        color: $gray-light;
        background: $gray-light;

        &.color-disabled {
          background: lighten($color-success, 15%);
        }

        > svg {
          fill: $white;
        }
      }
    }

    &.warning {
      background: $color-warning;
      border-color: $color-warning;
      color: $white;

      >svg {
        fill: $white;
      }

      &:hover {
        background: darken($color-warning, 15%);
        color: darken($white, 15%);

        svg {
          fill: $gray-very-light;
        }
      }

      &:active {
        background: darken($color-warning, 15%);
        svg {
          fill: $white;
        }
      }

      &[disabled] {
        border-color: $gray-light;
        color: $gray-light;
        background: $gray-light;

        > svg {
          fill: $white;
        }
      }
    }

    &.error {
      background: $color-error;
      border-color: $color-error;
      color: $white;

      svg {
        fill: $white;
      }

      &:hover {
        background: darken($color-error, 15%);
        color: $white;

        svg {
          fill: $gray-very-light;
        }
      }

      &:active {
        background: darken($color-error, 15%);
        svg {
          fill: $white;
        }
      }
    }

    &.pending {

      /* pending button is only disabled for now */

      /*background: $color-warning;
      border-color: $color-warning;
      color: $white;

      >svg {
        fill: $white;
      }

      &:hover {
        background: darken($color-warning, 15%);
        color: darken($white, 15%);

        svg {
          fill: $gray-very-light;
        }
      }

      &:active {
        background: darken($color-warning, 15%);
        svg {
          fill: $white;
        }
      }*/

      &[disabled] {
        border-color: $gray-light;
        color: $gray-light;
        background: lighten($cyan, 30%);

        > svg {
          fill: $white;
        }
      }
    }

    &[disabled] {
      border-color: $gray-light;
      color: $gray-light;
      pointer-events: none;

      > svg {
        fill: $gray-light;
      }
    }

    svg.right-icon {
      position: absolute;
      right: 0.75rem;
    }

    .fill-progress {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 0;
      background: #000;
      opacity: .4;

      &.complete {
        animation: buttonFill 14s linear;
        animation-iteration-count: infinite;

        /*&.always {*/
        /*  animation-iteration-count: infinite;*/
        /*}*/
        //width: 100%;
        //transition: width 14s;
      }
    }
  }

  @keyframes buttonFill {
    0% {
      width: 0;
    }
    100% {
      width: 100%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/basic/Btn.vue